import { useEffect, useState } from "react";

// Hooks & Helpers
import { useToast } from "sj-ui-toolkit/src/hooks";
import { fetchImpacts, saveImpacts, restoreImpacts, type Impact } from "@/api/activities";

// Local Components
import TabSkeleton from "./TabSkeleton";

// 3rd Party Imports
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Card } from "primereact/card";
import { confirmDialog } from "primereact/confirmdialog";

interface ImpactsTabProps {
  onUnsavedChange?: (hasChanges: boolean) => void;
}

export default function ImpactsTab({ onUnsavedChange }: ImpactsTabProps) {
  const [impacts, setImpacts] = useState<Impact[]>([]);
  const [maxImpactId, setMaxImpactId] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { showToast } = useToast();

  const markChanged = () => onUnsavedChange?.(true);
  const markSaved = () => onUnsavedChange?.(false);

  useEffect(() => {
    const loadImpacts = async () => {
      try {
        setIsLoading(true);
        const data = await fetchImpacts();
        setImpacts(data.impacts || []);
        setMaxImpactId(data.maxImpactId || 0);
      } catch (error) {
        console.error("Failed to load impacts:", error);
        showToast({ severity: "error", detail: "Failed to load impacts" });
      } finally {
        setIsLoading(false);
      }
    };

    loadImpacts();
  }, []);

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      const data = await saveImpacts(impacts);
      setImpacts(data.impacts || []);
      setMaxImpactId(data.maxImpactId || 0);
      markSaved();
      showToast({ severity: "success", detail: "Impacts saved successfully" });
    } catch (error) {
      console.error("Failed to save impacts:", error);
      showToast({ severity: "error", detail: "Failed to save impacts" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = () => {
    confirmDialog({
      message:
        "If you proceed, you will lose saved changes and have Security Journey default configurations restored.",
      header: "Are you sure?",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          const data = await restoreImpacts();
          setImpacts(data.impacts || []);
          setMaxImpactId(data.maxImpactId || 0);
          markSaved();
          showToast({ severity: "success", detail: "Impacts restored to defaults" });
        } catch (error) {
          console.error("Failed to restore impacts:", error);
          showToast({ severity: "error", detail: "Failed to restore impacts" });
        }
      },
    });
  };

  const addImpact = () => {
    const newId = maxImpactId + 1;
    setImpacts([...impacts, { id: newId, name: "" }]);
    setMaxImpactId(newId);
    markChanged();
  };

  const updateImpact = (id: number, name: string) => {
    setImpacts(impacts.map(impact => (impact.id === id ? { ...impact, name } : impact)));
    markChanged();
  };

  const deleteImpact = (id: number) => {
    setImpacts(impacts.filter(impact => impact.id !== id));
    markChanged();
  };

  if (isLoading) {
    return <TabSkeleton rows={4} />;
  }

  return (
    <div className="p-4">
      <Card>
        <div className="mb-4">
          <p className="text-secondary mb-4">Configure impact levels for activities.</p>

          <div className="flex flex-col gap-3">
            {impacts.map(impact => (
              <div key={impact.id} className="border-surface-border rounded-lg p-3">
                <div className="flex align-items-center gap-3">
                  <InputText
                    value={impact.name}
                    onChange={e => updateImpact(impact.id, e.target.value)}
                    placeholder="Impact name"
                    className="flex-1"
                    maxLength={15}
                  />
                  <Button
                    icon="pi pi-trash"
                    onClick={() => deleteImpact(impact.id)}
                    severity="danger"
                    text
                    rounded
                  />
                </div>
              </div>
            ))}

            <Button
              label="Add Impact"
              icon="pi pi-plus"
              onClick={addImpact}
              severity="secondary"
              className="w-fit"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-4 justify-end">
          <Button label="Restore Defaults" onClick={handleRestore} severity="secondary" />
          <Button
            label={isSaving ? "Saving..." : "Save"}
            onClick={() => void handleSave()}
            disabled={isSaving}
          />
        </div>
      </Card>
    </div>
  );
}
